import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, unwrap, errMsg } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/lib/auth";
import { levelDisplayName, levelWord, levelSystemOf } from "@/lib/levelLabels";
import { useLookupValues } from "@/lib/platformConfig";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  FileCheck2,
  FileUp,
  Loader2,
  RefreshCw,
  Save,
  ShieldCheck,
  Trash2,
  UserRound,
  UsersRound,
} from "lucide-react";

const createEmptyForm = () => ({
  people: {
    child: { first_name: "", last_name: "", date_of_birth: "", gender: "", place_of_birth: "", nationality: "" },
    father: { full_name: "", email: "", phone: "", nationality: "", education_level: "", occupation: "", work_details: "", is_primary_contact: true, is_emergency_contact: true },
    mother: { full_name: "", email: "", phone: "", nationality: "", education_level: "", occupation: "", work_details: "", is_primary_contact: false, is_emergency_contact: false },
  },
  placement: { grade_applied_id: "", academic_year_id: "" },
  address: { street: "", apartment: "" },
  documents: [],
});

const PERSON_ROLES = ["child", "father", "mother"];

const CHILD_DOCUMENT_EXCLUSIONS = new Set(["insurance", "medical"]);
const DEFAULT_REQUIREMENT_BY_KIND = {
  emirates_id: "optional",
  passport: "always",
  visa: "optional",
};

function Field({ label, value, onChange, type = "text", required = true, testId, placeholder }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]">
        {label}{required ? " *" : ""}
      </Label>
      <Input value={value || ""} onChange={(event) => onChange(event.target.value)} type={type} placeholder={placeholder} data-testid={testId} />
    </div>
  );
}

function SectionHeading({ icon: Icon, title, description }) {
  return (
    <div className="flex items-start gap-3 border-b border-[var(--subtle)] pb-5">
      <div className="rounded-md bg-[#1B3B36] p-2.5 text-white"><Icon className="h-5 w-5" /></div>
      <div>
        <h2 className="font-display text-xl font-bold text-[var(--text-primary)]">{title}</h2>
        <p className="mt-1 text-sm text-[var(--text-muted)]">{description}</p>
      </div>
    </div>
  );
}

export default function AdmissionApplicationWizard() {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const { t, direction, language } = useI18n();
  const { user } = useAuth();
  const mode = levelSystemOf(user);
  const [form, setForm] = React.useState(createEmptyForm);
  const [draftId, setDraftId] = React.useState(applicationId || null);
  const [revision, setRevision] = React.useState(null);
  const [step, setStep] = React.useState(0);
  const [saving, setSaving] = React.useState(false);
  const [uploadingKey, setUploadingKey] = React.useState("");
  const [dirty, setDirty] = React.useState(false);
  const [serverFields, setServerFields] = React.useState({});
  const [documentRole, setDocumentRole] = React.useState("child");
  const [documentReached, setDocumentReached] = React.useState(0);
  const [reviewUnlocked, setReviewUnlocked] = React.useState(false);

  const nationalityLookup = useLookupValues("nationalities");
  const childDocumentLookup = useLookupValues("student_document_kinds");
  const parentDocumentLookup = useLookupValues("parent_document_kinds");
  const legacyDocumentLookup = useLookupValues("admission_document_kinds");
  const genderLookup = useLookupValues("admission_genders");
  const nationalities = React.useMemo(() => {
    const values = nationalityLookup.data?.values || [];
    // Keep older tenant lookup snapshots compatible until the backend seed
    // migration has propagated; the canonical value remains DB-managed.
    return values.some((row) => row.value === "comoros")
      ? values
      : [...values, { key: "comoros", value: "comoros", label: "Comoros", sort_order: 70, metadata: { is_uae_citizen: false } }];
  }, [nationalityLookup.data?.values]);
  const genders = React.useMemo(() => genderLookup.data?.values || [], [genderLookup.data?.values]);
  const normalizeDocKind = React.useCallback((row, personRoles) => {
    const metadata = row?.metadata && typeof row.metadata === "object" ? { ...row.metadata } : {};
    const requirement = ["always", "optional", "non_uae_citizen"].includes(metadata.requirement)
      ? metadata.requirement
      : (DEFAULT_REQUIREMENT_BY_KIND[row.value] || "optional");
    return {
      ...row,
      metadata: {
        ...metadata,
        person_roles: personRoles,
        requirement,
        upload_only: Boolean(metadata.upload_only) || row.value === "vaccination",
      },
    };
  }, []);

  const childDocumentKinds = React.useMemo(() => {
    const rows = (childDocumentLookup.data?.values || [])
      .filter((row) => typeof row?.value === "string" && row.value && !CHILD_DOCUMENT_EXCLUSIONS.has(row.value))
      .map((row) => normalizeDocKind(row, ["child"]));
    if (rows.length) return rows;
    return (legacyDocumentLookup.data?.values || [])
      .filter((row) => typeof row?.value === "string" && row.value && !CHILD_DOCUMENT_EXCLUSIONS.has(row.value) && (row?.metadata?.person_roles || []).includes("child"))
      .map((row) => normalizeDocKind(row, ["child"]));
  }, [childDocumentLookup.data?.values, legacyDocumentLookup.data?.values, normalizeDocKind]);

  const parentDocumentKinds = React.useMemo(() => {
    const primary = (parentDocumentLookup.data?.values || [])
      .filter((row) => typeof row?.value === "string" && row.value)
      .map((row) => {
        const roles = (row?.metadata?.person_roles || ["father", "mother"]).filter((role) => role === "father" || role === "mother");
        return normalizeDocKind(row, roles.length ? roles : ["father", "mother"]);
      });
    if (primary.length) return primary;
    return (legacyDocumentLookup.data?.values || [])
      .filter((row) => typeof row?.value === "string" && row.value !== "vaccination" && (row?.metadata?.person_roles || []).some((role) => role === "father" || role === "mother"))
      .map((row) => {
        const roles = (row?.metadata?.person_roles || []).filter((role) => role === "father" || role === "mother");
        return normalizeDocKind(row, roles.length ? roles : ["father", "mother"]);
      });
  }, [parentDocumentLookup.data?.values, legacyDocumentLookup.data?.values, normalizeDocKind]);

  const documentKindsByRole = React.useMemo(() => ({
    child: childDocumentKinds,
    father: parentDocumentKinds,
    mother: parentDocumentKinds,
  }), [childDocumentKinds, parentDocumentKinds]);

  const gradesQuery = useQuery({ queryKey: ["grades"], queryFn: async () => unwrap(await api.get("/grades")) });
  const yearsQuery = useQuery({ queryKey: ["ay"], queryFn: async () => unwrap(await api.get("/academic-years")) });
  const grades = React.useMemo(() => gradesQuery.data || [], [gradesQuery.data]);
  const years = yearsQuery.data || [];
  const draftQuery = useQuery({
    queryKey: ["application-draft", applicationId],
    queryFn: async () => unwrap(await api.get(`/applications/${applicationId}`)),
    enabled: !!applicationId,
  });

  React.useEffect(() => {
    if (!draftQuery.data) return;
    if (draftQuery.data.submission_state !== "draft" || draftQuery.data.schema_version !== 2) {
      toast.error(t("admissionWizard.notDraft"));
      navigate("/admissions", { replace: true });
      return;
    }
    const empty = createEmptyForm();
    const draftPeople = draftQuery.data.people || {};
    const draftFather = { ...empty.people.father, ...(draftPeople.father || {}) };
    if (!draftFather.work_details && draftFather.workplace) draftFather.work_details = draftFather.workplace;
    setForm({
      ...empty,
      ...draftQuery.data,
      people: {
        ...empty.people,
        ...draftPeople,
        father: draftFather,
        mother: { ...empty.people.mother, ...(draftPeople.mother || {}) },
      },
    });
    setRevision(draftQuery.data.revision);
    setDraftId(draftQuery.data.id);
    setDirty(false);
  }, [draftQuery.data, navigate, t]);

  React.useEffect(() => {
    const warn = (event) => {
      if (!dirty) return;
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const updatePerson = (role, key, value) => {
    setDirty(true);
    setForm((current) => ({ ...current, people: { ...current.people, [role]: { ...current.people[role], [key]: value } } }));
  };
  const updateSection = (section, key, value) => {
    setDirty(true);
    setForm((current) => ({ ...current, [section]: { ...current[section], [key]: value } }));
  };

  const childDob = form.people.child.date_of_birth || "";
  const dobValid = /^\d{4}-\d{2}-\d{2}$/.test(childDob);

  // In Age mode many levels can share one band; offer one option per band so
  // the dropdown stays readable. The chosen value is one of the band's levels.
  const gradeOptions = React.useMemo(() => {
    if (mode !== "age_band") return grades;
    const seen = new Set();
    const options = [];
    grades.forEach((g) => {
      const key = g.age_band_key || `${g.age_min_days || ""}-${g.age_max_days || ""}`;
      if (key && seen.has(key)) return;
      if (key) seen.add(key);
      options.push(g);
    });
    return options;
  }, [mode, grades]);

  const ageSuggestQuery = useQuery({
    queryKey: ["suggest-age-band", childDob],
    queryFn: async () => unwrap(await api.get("/academic/suggest-age-band", { params: { date_of_birth: childDob } })),
    enabled: mode === "age_band" && dobValid,
  });
  const ageBandSuggestion = ageSuggestQuery.data || null;
  const autoSuggestedGradeRef = React.useRef(false);

  React.useEffect(() => {
    if (mode !== "age_band" || !ageBandSuggestion || !grades.length) return;
    const matching = grades.find(
      (g) => g.age_min_days === ageBandSuggestion.min_days && g.age_max_days === ageBandSuggestion.max_days,
    );
    if (!matching) return;
    if (form.placement.grade_applied_id && !autoSuggestedGradeRef.current) return;
    autoSuggestedGradeRef.current = true;
    if (form.placement.grade_applied_id !== matching.id) {
      setDirty(true);
      setForm((current) => ({
        ...current,
        placement: { ...current.placement, grade_applied_id: matching.id },
      }));
    }
  }, [mode, ageBandSuggestion, grades, form.placement.grade_applied_id]);
  const setPrimary = (role) => {
    setDirty(true);
    setForm((current) => ({
      ...current,
      people: {
        ...current.people,
        father: { ...current.people.father, is_primary_contact: role === "father" },
        mother: { ...current.people.mother, is_primary_contact: role === "mother" },
      },
    }));
  };

  const persist = React.useCallback(async (payload = form) => {
    setSaving(true);
    try {
      const saved = draftId
        ? unwrap(await api.patch(`/applications/${draftId}/draft`, { ...payload, revision }))
        : unwrap(await api.post("/applications/drafts", payload));
      setDraftId(saved.id);
      setRevision(saved.revision);
      setDirty(false);
      return saved;
    } finally {
      setSaving(false);
    }
  }, [draftId, form, revision]);

  const saveAndExit = async () => {
    try {
      await persist();
      toast.success(t("admissionWizard.draftSaved"));
      navigate("/admissions");
    } catch (error) {
      toast.error(errMsg(error));
    }
  };

  const next = async () => {
    try {
      await persist();
      if (step === 2) {
        const currentRoleIndex = PERSON_ROLES.indexOf(documentRole);
        if (currentRoleIndex < PERSON_ROLES.length - 1) {
          const nextRoleIndex = currentRoleIndex + 1;
          setDocumentReached((current) => Math.max(current, nextRoleIndex));
          setDocumentRole(PERSON_ROLES[nextRoleIndex]);
          window.scrollTo({ top: 0, behavior: "smooth" });
          return;
        }
        setReviewUnlocked(true);
        setStep(3);
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      setStep((current) => Math.min(current + 1, 3));
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (error) {
      toast.error(errMsg(error));
    }
  };

  const documentFor = (role, kind) => form.documents.find((row) => row.person_role === role && row.kind === kind);
  const changeDocument = (role, kind, changes) => {
    setDirty(true);
    setForm((current) => {
      const documents = [...current.documents];
      const index = documents.findIndex((row) => row.person_role === role && row.kind === kind);
      const nextDocument = { person_role: role, kind, number: "", expiry_date: "", file_ids: [], file_names: [], ...(index >= 0 ? documents[index] : {}), ...changes };
      if (index >= 0) documents[index] = nextDocument;
      else documents.push(nextDocument);
      return { ...current, documents };
    });
  };

  const uploadFiles = async (role, kind, files) => {
    if (!files.length) return;
    const key = `${role}-${kind}`;
    setUploadingKey(key);
    try {
      if (!draftId) await persist();
      const current = documentFor(role, kind) || { file_ids: [], file_names: [] };
      const available = Math.max(5 - (current.file_ids?.length || 0), 0);
      const selected = [...files].slice(0, available);
      const uploaded = [];
      for (const file of selected) {
        const body = new FormData();
        body.append("file", file);
        body.append("is_sensitive", "true");
        const response = unwrap(await api.post("/files/upload", body, { headers: { "Content-Type": "multipart/form-data" } }));
        uploaded.push({ id: response.id, name: file.name });
      }
      changeDocument(role, kind, {
        file_ids: [...(current.file_ids || []), ...uploaded.map((row) => row.id)],
        file_names: [...(current.file_names || []), ...uploaded.map((row) => row.name)],
      });
    } catch (error) {
      toast.error(errMsg(error));
    } finally {
      setUploadingKey("");
    }
  };

  const removeFile = (role, kind, index) => {
    const current = documentFor(role, kind);
    if (!current) return;
    changeDocument(role, kind, {
      file_ids: current.file_ids.filter((_, itemIndex) => itemIndex !== index),
      file_names: (current.file_names || []).filter((_, itemIndex) => itemIndex !== index),
    });
  };

  const applicableKinds = (role) => documentKindsByRole[role] || [];
  const requiredKind = (role, row) => row.metadata?.requirement === "always";
  const completion = (role) => {
    const required = applicableKinds(role).filter((row) => requiredKind(role, row));
    const complete = required.filter((row) => {
      const document = documentFor(role, row.value);
      return document?.number && document?.expiry_date && document?.file_ids?.length;
    });
    return `${complete.length}/${required.length}`;
  };

  const displayFieldName = (key) => {
    const parts = key.split(".");
    const role = PERSON_ROLES.find((item) => parts.includes(item));
    const token = parts[parts.length - 1];
    const labels = {
      first_name: t("admissionWizard.fields.firstName"), last_name: t("admissionWizard.fields.lastName"),
      full_name: t("admissionWizard.fields.fullName"), date_of_birth: t("admissionWizard.fields.dob"),
      place_of_birth: t("admissionWizard.fields.birthPlace"), gender: t("admissionWizard.fields.gender"),
      nationality: t("admissionWizard.fields.nationality"), grade_applied_id: t("admissionWizard.fields.grade"),
      academic_year_id: t("admissionWizard.fields.year"), street: t("admissionWizard.fields.street"),
      apartment: t("admissionWizard.fields.apartment"), email: t("admissionWizard.fields.email"),
      phone: t("admissionWizard.fields.phone"), education_level: t("admissionWizard.fields.education"),
      occupation: t("admissionWizard.fields.occupation"), workplace: t("admissionWizard.fields.workplace"),
      work_details: t("admissionWizard.fields.workDetails"), contacts: t("admissionWizard.parents.title"),
    };
    const subject = role ? `${t(`admissionWizard.people.${role}`)} — ` : "";
    return `${subject}${labels[token] || token.replaceAll("_", " ")}`;
  };

  const submit = async () => {
    try {
      const saved = await persist();
      const submitted = unwrap(await api.post(`/applications/${saved.id}/submit`));
      setDirty(false);
      toast.success(t("admissionWizard.submitted", { number: submitted.application_no }));
      navigate("/admissions");
    } catch (error) {
      const fields = error?.response?.data?.detail?.error?.fields || {};
      setServerFields(fields);
      toast.error(errMsg(error));
      const fieldNames = Object.keys(fields);
      const hasChildOrPlacementError = fieldNames.some((key) => key.startsWith("people.child") || key.startsWith("placement.") || key.startsWith("address."));
      const hasParentError = fieldNames.some((key) => key.startsWith("people.father") || key.startsWith("people.mother") || key.startsWith("people.contacts"));
      if (hasChildOrPlacementError) setStep(0);
      else if (hasParentError) setStep(1);
      else if (fieldNames.some((key) => key.startsWith("documents."))) {
        const documentError = fieldNames.find((key) => key.startsWith("documents."));
        const errorRole = PERSON_ROLES.find((role) => documentError.startsWith(`documents.${role}.`));
        if (errorRole) {
          const errorIndex = PERSON_ROLES.indexOf(errorRole);
          setDocumentReached((current) => Math.max(current, errorIndex));
          setDocumentRole(errorRole);
        }
        setStep(2);
      }
      else setStep(0);
    }
  };

  const requiredQueries = [nationalityLookup, childDocumentLookup, parentDocumentLookup, legacyDocumentLookup, genderLookup, gradesQuery, yearsQuery, ...(applicationId ? [draftQuery] : [])];
  const configurationLoading = requiredQueries.some((query) => query.isLoading);
  const configurationError = requiredQueries.some((query) => query.isError);

  if (configurationLoading || configurationError) {
    return (
      <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center bg-[var(--bg)] p-6" dir={direction} data-testid="admission-wizard-state">
        <Card className="w-full max-w-lg border-[var(--subtle)] shadow-none">
          <CardContent className="flex flex-col items-center py-12 text-center">
            {configurationLoading ? (
              <><Loader2 className="h-8 w-8 animate-spin text-[var(--accent)]" /><p className="mt-4 font-semibold">{t("admissionWizard.loading")}</p></>
            ) : (
              <><p className="font-semibold text-red-700">{t("admissionWizard.loadError")}</p><Button className="mt-4" variant="outline" onClick={() => requiredQueries.forEach((query) => query.refetch())}><RefreshCw className="me-2 h-4 w-4" />{t("admissionWizard.retry")}</Button></>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const steps = [
    { label: t("admissionWizard.steps.child"), icon: UserRound },
    { label: t("admissionWizard.steps.parents"), icon: UsersRound },
    { label: t("admissionWizard.steps.documents"), icon: FileCheck2 },
    { label: t("admissionWizard.steps.review"), icon: ShieldCheck },
  ];

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[var(--bg)]" dir={direction} data-testid="admission-wizard">
      <header className="sticky top-0 z-20 border-b border-[var(--subtle)] bg-white/90 px-4 py-4 backdrop-blur-xl md:px-8">
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/admissions")} data-testid="admission-wizard-back"><ArrowLeft className="h-5 w-5 rtl:rotate-180" /></Button>
            <div><h1 className="font-display text-xl font-bold md:text-2xl">{t("admissionWizard.title")}</h1><p className="text-xs text-[var(--text-muted)]">{draftId ? t("admissionWizard.savedDraft") : t("admissionWizard.newDraft")}</p></div>
          </div>
          <Button variant="outline" onClick={saveAndExit} disabled={saving} data-testid="admission-save-exit"><Save className="me-2 h-4 w-4" />{saving ? t("common.saving") : t("admissionWizard.saveExit")}</Button>
        </div>
      </header>

      <main className="mx-auto grid max-w-7xl gap-6 px-4 py-6 md:px-8 lg:grid-cols-[240px_minmax(0,1fr)] lg:py-8">
        <aside className="rounded-lg border border-[var(--subtle)] bg-white p-4 lg:sticky lg:top-28 lg:h-fit" aria-label={t("admissionWizard.progress")}>
          <Progress value={((step + 1) / steps.length) * 100} className="mb-5 bg-[var(--subtle)] [&>div]:bg-[#C05621]" />
          <div className="grid grid-cols-4 gap-2 lg:grid-cols-1">
            {steps.map(({ label, icon: Icon }, index) => (
              <button key={label} type="button" disabled={index === 3 && !reviewUnlocked} onClick={() => (index < 3 || reviewUnlocked) && setStep(index)} className={`flex min-h-12 items-center gap-3 rounded-md px-3 py-2 text-start text-sm transition-colors ${index === step ? "bg-[#1B3B36] text-white" : index < step ? "bg-green-50 text-green-800" : "text-[var(--text-muted)]"} disabled:cursor-not-allowed disabled:opacity-50`} data-testid={`admission-step-${index}`}>
                {index < step ? <Check className="h-4 w-4 shrink-0" /> : <Icon className="h-4 w-4 shrink-0" />}
                <span className="hidden lg:inline">{label}</span>
              </button>
            ))}
          </div>
        </aside>

        <Card className="border-[var(--subtle)] shadow-none">
          <CardContent className="space-y-6 p-5 md:p-8">
            {Object.keys(serverFields).length > 0 && <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700" role="alert" data-testid="admission-validation-errors"><div className="font-semibold">{t("admissionWizard.review.missing")}</div><ul className="mt-2 list-disc ps-5">{Object.entries(serverFields).slice(0, 12).map(([key, message]) => <li key={key}>{displayFieldName(key)}: {message}</li>)}</ul></div>}
            {step === 0 && (
              <div className="space-y-6" data-testid="admission-child-step">
                <SectionHeading icon={UserRound} title={t("admissionWizard.child.title")} description={t("admissionWizard.child.description")} />
                <div className="grid gap-4 md:grid-cols-2">
                  <Field label={t("admissionWizard.fields.firstName")} value={form.people.child.first_name} onChange={(value) => updatePerson("child", "first_name", value)} testId="admission-child-first-name" />
                  <Field label={t("admissionWizard.fields.lastName")} value={form.people.child.last_name} onChange={(value) => updatePerson("child", "last_name", value)} testId="admission-child-last-name" />
                  <Field label={t("admissionWizard.fields.dob")} type="date" value={form.people.child.date_of_birth} onChange={(value) => updatePerson("child", "date_of_birth", value)} testId="admission-child-dob" />
                  <Field label={t("admissionWizard.fields.birthPlace")} value={form.people.child.place_of_birth} onChange={(value) => updatePerson("child", "place_of_birth", value)} testId="admission-child-birth-place" />
                  <div className="space-y-1.5"><Label>{t("admissionWizard.fields.gender")} *</Label><Select value={form.people.child.gender} onValueChange={(value) => updatePerson("child", "gender", value)}><SelectTrigger data-testid="admission-child-gender"><SelectValue placeholder={t("admissionWizard.fields.select")} /></SelectTrigger><SelectContent>{genders.map((row) => <SelectItem key={row.value} value={row.value}>{row.label}</SelectItem>)}</SelectContent></Select></div>
                  <div className="space-y-1.5"><Label>{t("admissionWizard.fields.nationality")} *</Label><Select value={form.people.child.nationality} onValueChange={(value) => updatePerson("child", "nationality", value)}><SelectTrigger data-testid="admission-child-nationality"><SelectValue placeholder={t("admissionWizard.fields.select")} /></SelectTrigger><SelectContent>{nationalities.map((row) => <SelectItem key={row.value} value={row.value}>{row.label}</SelectItem>)}</SelectContent></Select></div>
                  <div className="space-y-1.5"><Label>{mode === "age_band" ? levelWord(mode, language) : t("admissionWizard.fields.grade")} *</Label><Select value={form.placement.grade_applied_id} onValueChange={(value) => { autoSuggestedGradeRef.current = false; updateSection("placement", "grade_applied_id", value); }}><SelectTrigger data-testid="admission-grade"><SelectValue placeholder={t("admissionWizard.fields.select")} /></SelectTrigger><SelectContent>{gradeOptions.map((row) => <SelectItem key={row.id} value={row.id}>{levelDisplayName(row, language)}</SelectItem>)}</SelectContent></Select>
                    {mode === "age_band" && dobValid && !ageSuggestQuery.isLoading && !ageBandSuggestion && (
                      <p className="text-xs text-amber-700" data-testid="admission-age-warning">
                        {t("academic.outsideAllBands")}
                      </p>
                    )}
                  </div>
                  <div className="space-y-1.5"><Label>{t("admissionWizard.fields.year")} *</Label><Select value={form.placement.academic_year_id} onValueChange={(value) => updateSection("placement", "academic_year_id", value)}><SelectTrigger data-testid="admission-year"><SelectValue placeholder={t("admissionWizard.fields.select")} /></SelectTrigger><SelectContent>{years.map((row) => <SelectItem key={row.id} value={row.id}>{row.name}</SelectItem>)}</SelectContent></Select></div>
                  <Field label={t("admissionWizard.fields.street")} value={form.address.street} onChange={(value) => updateSection("address", "street", value)} testId="admission-address-street" />
                  <Field label={t("admissionWizard.fields.apartment")} value={form.address.apartment} onChange={(value) => updateSection("address", "apartment", value)} testId="admission-address-apartment" />
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="space-y-6" data-testid="admission-parents-step">
                <SectionHeading icon={UsersRound} title={t("admissionWizard.parents.title")} description={t("admissionWizard.parents.description")} />
                {PERSON_ROLES.slice(1).map((role) => (
                  <div key={role} className="rounded-lg border border-[var(--subtle)] p-4 md:p-5">
                    <div className="mb-4 flex items-center justify-between"><h3 className="font-display text-lg font-bold">{t(`admissionWizard.people.${role}`)}</h3>{form.people[role].is_primary_contact && <span className="rounded-md bg-green-50 px-2 py-1 text-xs font-semibold text-green-700">{t("admissionWizard.parents.primary")}</span>}</div>
                    <div className="grid gap-4 md:grid-cols-2">
                      <Field label={t("admissionWizard.fields.fullName")} value={form.people[role].full_name} onChange={(value) => updatePerson(role, "full_name", value)} testId={`admission-${role}-name`} />
                      <Field label={t("admissionWizard.fields.email")} type="email" value={form.people[role].email} onChange={(value) => updatePerson(role, "email", value)} testId={`admission-${role}-email`} />
                      <Field label={t("admissionWizard.fields.phone")} value={form.people[role].phone} onChange={(value) => updatePerson(role, "phone", value)} testId={`admission-${role}-phone`} placeholder="+971…" />
                      <div className="space-y-1.5"><Label>{t("admissionWizard.fields.nationality")} *</Label><Select value={form.people[role].nationality} onValueChange={(value) => updatePerson(role, "nationality", value)}><SelectTrigger data-testid={`admission-${role}-nationality`}><SelectValue placeholder={t("admissionWizard.fields.select")} /></SelectTrigger><SelectContent>{nationalities.map((row) => <SelectItem key={row.value} value={row.value}>{row.label}</SelectItem>)}</SelectContent></Select></div>
                      <Field label={t("admissionWizard.fields.education")} value={form.people[role].education_level} onChange={(value) => updatePerson(role, "education_level", value)} testId={`admission-${role}-education`} />
                      <Field label={t("admissionWizard.fields.occupation")} value={form.people[role].occupation} onChange={(value) => updatePerson(role, "occupation", value)} testId={`admission-${role}-occupation`} />
                      <Field label={t("admissionWizard.fields.workDetails")} value={form.people[role].work_details} onChange={(value) => updatePerson(role, "work_details", value)} testId={`admission-${role}-work`} />
                    </div>
                    <div className="mt-5 flex flex-wrap gap-5 border-t border-[var(--subtle)] pt-4">
                      <label className="flex cursor-pointer items-center gap-2 text-sm"><Checkbox checked={form.people[role].is_primary_contact} onCheckedChange={() => setPrimary(role)} data-testid={`admission-${role}-primary`} />{t("admissionWizard.parents.makePrimary")}</label>
                      <label className="flex cursor-pointer items-center gap-2 text-sm"><Checkbox checked={form.people[role].is_emergency_contact} onCheckedChange={(value) => updatePerson(role, "is_emergency_contact", Boolean(value))} data-testid={`admission-${role}-emergency`} />{t("admissionWizard.parents.emergency")}</label>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {step === 2 && (
              <div className="space-y-6" data-testid="admission-documents-step">
                <SectionHeading icon={FileCheck2} title={t("admissionWizard.documents.title")} description={t("admissionWizard.documents.description")} />
                <Tabs value={documentRole} onValueChange={setDocumentRole}>
                  <TabsList className="grid h-auto w-full grid-cols-3 bg-[var(--subtle)] p-1">
                    {PERSON_ROLES.map((role, index) => <TabsTrigger key={role} value={role} disabled={index > documentReached} className="gap-2 py-2" data-testid={`admission-doc-tab-${role}`}>{t(`admissionWizard.people.${role}`)} <span className="text-[10px] opacity-70">{completion(role)}</span></TabsTrigger>)}
                  </TabsList>
                  {PERSON_ROLES.map((role) => (
                    <TabsContent key={role} value={role} className="mt-5 space-y-4">
                      {applicableKinds(role).map((row) => {
                        const required = requiredKind(role, row);
                        const document = documentFor(role, row.value) || {};
                        return (
                          <div key={row.value} className="rounded-lg border border-[var(--subtle)] bg-white p-4" data-testid={`admission-doc-${role}-${row.value}`}>
                            <div className="mb-4 flex items-start justify-between gap-3"><div><h3 className="font-semibold">{row.label}{required ? " *" : ""}</h3><p className="text-xs text-[var(--text-muted)]">{required ? t("admissionWizard.documents.required") : t("admissionWizard.documents.optionalIfApplicable")}</p></div><FileCheck2 className="h-5 w-5 text-[var(--accent)]" /></div>
                            {!row.metadata?.upload_only && <div className="grid gap-3 md:grid-cols-2">
                              <Field label={t("admissionWizard.documents.number")} value={document.number} onChange={(value) => changeDocument(role, row.value, { number: value })} required={required} testId={`admission-doc-${role}-${row.value}-number`} />
                              <Field label={t("admissionWizard.documents.expiry")} type="date" value={document.expiry_date} onChange={(value) => changeDocument(role, row.value, { expiry_date: value })} required={required} testId={`admission-doc-${role}-${row.value}-expiry`} />
                            </div>}
                            <div className="mt-4">
                              <Input id={`upload-${role}-${row.value}`} type="file" multiple accept="application/pdf,image/jpeg,image/png" className="sr-only" onChange={(event) => uploadFiles(role, row.value, event.target.files)} data-testid={`admission-doc-${role}-${row.value}-files`} />
                              <Label htmlFor={`upload-${role}-${row.value}`} className="flex min-h-12 cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-[var(--accent)] bg-orange-50/40 px-4 text-sm font-semibold text-[var(--accent)]"><FileUp className="h-4 w-4" />{uploadingKey === `${role}-${row.value}` ? t("admissionWizard.documents.uploading") : t("admissionWizard.documents.upload")}</Label>
                              <div className="mt-2 space-y-2">{(document.file_names || []).map((name, index) => <div key={`${name}-${index}`} className="flex items-center justify-between rounded-md bg-[var(--bg)] px-3 py-2 text-xs"><span className="truncate">{name}</span><Button variant="ghost" size="icon" onClick={() => removeFile(role, row.value, index)} data-testid={`remove-${role}-${row.value}-${index}`}><Trash2 className="h-4 w-4 text-red-500" /></Button></div>)}</div>
                            </div>
                          </div>
                        );
                      })}
                    </TabsContent>
                  ))}
                </Tabs>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-6" data-testid="admission-review-step">
                <SectionHeading icon={ShieldCheck} title={t("admissionWizard.review.title")} description={t("admissionWizard.review.description")} />
                <div className="grid gap-4 md:grid-cols-3">{PERSON_ROLES.map((role) => <div key={role} className="rounded-lg border border-[var(--subtle)] p-4"><div className="text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">{t(`admissionWizard.people.${role}`)}</div><div className="mt-2 font-semibold">{role === "child" ? `${form.people.child.first_name} ${form.people.child.last_name}` : form.people[role].full_name || "—"}</div><div className="mt-1 text-xs text-[var(--text-muted)]">{t("admissionWizard.review.documents", { count: completion(role) })}</div></div>)}</div>
                <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-900"><div className="flex gap-3"><ShieldCheck className="mt-0.5 h-5 w-5 shrink-0" /><p>{t("admissionWizard.review.privacy")}</p></div></div>
              </div>
            )}
          </CardContent>

          <div className="sticky bottom-0 flex items-center justify-between gap-3 border-t border-[var(--subtle)] bg-white/95 p-4 backdrop-blur-xl md:px-8">
            <Button variant="outline" onClick={() => setStep((current) => Math.max(current - 1, 0))} disabled={step === 0 || saving} data-testid="admission-previous"><ArrowLeft className="me-2 h-4 w-4 rtl:rotate-180" />{t("admissionWizard.previous")}</Button>
            {step < 3 ? <Button onClick={next} disabled={saving} className="bg-[#C05621] text-white hover:bg-[#9C4221]" data-testid="admission-next">{saving ? t("common.saving") : t("admissionWizard.saveContinue")}<ArrowRight className="ms-2 h-4 w-4 rtl:rotate-180" /></Button> : <Button onClick={submit} disabled={saving} className="bg-[#1B3B36] text-white hover:bg-[#2C524C]" data-testid="admission-submit"><CheckCircle2 className="me-2 h-4 w-4" />{t("admissionWizard.submit")}</Button>}
          </div>
        </Card>
      </main>
    </div>
  );
}
